import type { IDate } from "../../types/date";
import type { IProduct } from "../../types/entities";
import { Action, Measures } from "../../types/entities";
import { isBoxId } from "../../domain/entity-ids";
import type {
  AiBoxDraft,
  AiFormFillResult,
  AiProductDraft,
  AdminDashboardSummary,
  AuthIdentity,
  AuthIdentityProvider,
  AuthResponse,
  AuthUser,
  BoxListItem,
  BoxProductItem,
  DashboardStats,
  DeletedBoxItem,
  DeletedProductItem,
  PagedResult,
  ProductJournalEntry,
  RecentJournalFeedEntry,
  SoftDeleteAuditItem,
  UserListItem,
} from "../WarehouseApi";

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function extractApiError(payload: unknown): string | null {
  if (!isRecord(payload)) return null;
  if (typeof payload.error === "string") {
    return payload.error;
  }
  if (payload.success === false && typeof payload.error === "string") {
    return payload.error;
  }
  return null;
}

function normalizeDate(value: unknown): IDate | null {
  if (!isRecord(value)) {
    return null;
  }

  const day = Number(value.day);
  const month = Number(value.month);
  const year = Number(value.year);
  const hour = Number(value.hour ?? 0);
  const minute = Number(value.minute ?? 0);

  if (
    !Number.isFinite(day) ||
    !Number.isFinite(month) ||
    !Number.isFinite(year) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }

  return {
    day: day as IDate["day"],
    month: month as IDate["month"],
    year,
    hour: hour as IDate["hour"],
    minute: minute as IDate["minute"],
  };
}

function parseDate(value: unknown): IDate | null {
  if (typeof value === "string") {
    try {
      return normalizeDate(JSON.parse(value));
    } catch {
      const jsDate = new Date(value);
      if (Number.isNaN(jsDate.getTime())) {
        return null;
      }
      return {
        day: jsDate.getDate() as IDate["day"],
        month: (jsDate.getMonth() + 1) as IDate["month"],
        year: jsDate.getFullYear(),
        hour: jsDate.getHours() as IDate["hour"],
        minute: jsDate.getMinutes() as IDate["minute"],
      };
    }
  }

  return normalizeDate(value);
}

function normalizeMeasure(value: unknown): Measures {
  if (value === Measures.liters) return Measures.liters;
  if (value === Measures.weight) return Measures.weight;
  if (value === Measures.quantity) return Measures.quantity;
  return Measures.quantity;
}

function normalizeQuantity(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 1;
  }
  return parsed;
}

export function normalizeTypeTags(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const tags = value
    .map((tag) => (typeof tag === "string" ? tag.trim() : ""))
    .filter((tag) => !!tag);

  return Array.from(new Set(tags));
}

function normalizeProductPhotos(
  value: unknown,
  fallbackPhoto: string,
): string[] {
  const photos = Array.isArray(value)
    ? value
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter((item) => !!item)
    : [];

  const unique = new Set<string>();
  const normalized: string[] = [];

  if (fallbackPhoto.trim()) {
    unique.add(fallbackPhoto.trim());
    normalized.push(fallbackPhoto.trim());
  }

  photos.forEach((photo) => {
    if (unique.has(photo)) {
      return;
    }

    unique.add(photo);
    normalized.push(photo);
  });

  return normalized;
}

export function normalizeProduct(value: unknown): IProduct {
  const raw = isRecord(value) ? value : {};
  const photo = typeof raw.photo === "string" ? raw.photo : "";
  const photos = normalizeProductPhotos(raw.photos, photo);

  return {
    id: String(raw.id || ""),
    name: String(raw.name || ""),
    photo: photos[0] || photo,
    photos,
    description: typeof raw.description === "string" ? raw.description : "",
    manufacturingDate: normalizeDate(raw.manufacturingDate),
    expiryDate: normalizeDate(raw.expiryDate),
    typeTags: normalizeTypeTags(raw.typeTags),
    measure: normalizeMeasure(raw.measure),
    quantity: normalizeQuantity(raw.quantity),
    parentId: raw.parentId ? String(raw.parentId) : null,
  };
}

function normalizeAction(value: unknown): Action {
  if (value === Action.take) return Action.take;
  if (value === Action.put) return Action.put;
  if (value === Action.destroy) return Action.destroy;
  return Action.put;
}

export function normalizeJournalEntry(value: unknown): ProductJournalEntry {
  const raw = isRecord(value) ? value : {};
  const userRoleRaw = raw.userRole;
  const userRole =
    userRoleRaw === "admin" ||
    userRoleRaw === "user" ||
    userRoleRaw === "viewer"
      ? userRoleRaw
      : null;
  const actorTypeRaw = raw.actorType;
  const userId =
    typeof raw.userId === "string" && raw.userId.trim()
      ? raw.userId.trim()
      : null;
  const userName =
    typeof raw.userName === "string" && raw.userName.trim()
      ? raw.userName.trim()
      : null;
  const actorType: "user" | "system" =
    actorTypeRaw === "user" || actorTypeRaw === "system"
      ? actorTypeRaw
      : userId || userName
        ? "user"
        : "system";
  const actorLabelRaw =
    typeof raw.actorLabel === "string" && raw.actorLabel.trim()
      ? raw.actorLabel.trim()
      : null;

  const actorLabel =
    actorLabelRaw ||
    (actorType === "system"
      ? "система"
      : userName
        ? userRole
          ? `${userName} (${userRole})`
          : userName
        : userId || "пользователь");

  return {
    id: String(raw.id || ""),
    date: parseDate(raw.date),
    userId,
    userName,
    userRole,
    actorType,
    actorLabel,
    action: normalizeAction(raw.action),
    productId: String(raw.productId || ""),
    fromTo: String(raw.fromTo || ""),
    value: Number(raw.value || 1),
    comment: typeof raw.comment === "string" ? raw.comment : null,
  };
}

export function normalizeRecentJournalFeedEntry(
  value: unknown,
): RecentJournalFeedEntry {
  const raw = isRecord(value) ? value : {};
  const eventType = typeof raw.eventType === "string" ? raw.eventType : "";

  if (eventType === "box.restored") {
    const userRoleRaw = raw.userRole;
    const userRole =
      userRoleRaw === "admin" ||
      userRoleRaw === "user" ||
      userRoleRaw === "viewer"
        ? userRoleRaw
        : null;
    const userId =
      typeof raw.userId === "string" && raw.userId.trim()
        ? raw.userId.trim()
        : null;
    const userName =
      typeof raw.userName === "string" && raw.userName.trim()
        ? raw.userName.trim()
        : null;
    const actorTypeRaw = raw.actorType;
    const actorType: "user" | "system" =
      actorTypeRaw === "user" || actorTypeRaw === "system"
        ? actorTypeRaw
        : userId || userName
          ? "user"
          : "system";
    const actorLabelRaw =
      typeof raw.actorLabel === "string" && raw.actorLabel.trim()
        ? raw.actorLabel.trim()
        : null;
    const actorLabel =
      actorLabelRaw ||
      (actorType === "system"
        ? "система"
        : userName
          ? userRole
            ? `${userName} (${userRole})`
            : userName
          : userId || "пользователь");

    return {
      id: String(raw.id || ""),
      eventType: "box.restored",
      date: parseDate(raw.date),
      userId,
      userName,
      userRole,
      actorType,
      actorLabel,
      boxId:
        typeof raw.boxId === "string" && raw.boxId.trim()
          ? raw.boxId.trim()
          : "",
      boxName:
        typeof raw.boxName === "string" && raw.boxName.trim()
          ? raw.boxName.trim()
          : null,
      comment:
        typeof raw.comment === "string" && raw.comment.trim()
          ? raw.comment.trim()
          : null,
      source:
        typeof raw.source === "string" && raw.source.trim()
          ? raw.source.trim()
          : null,
    };
  }

  const entry = normalizeJournalEntry(value);

  return {
    ...entry,
    eventType: "journal",
    productName:
      typeof raw.productName === "string" && raw.productName.trim()
        ? raw.productName.trim()
        : null,
    boxName:
      typeof raw.boxName === "string" && raw.boxName.trim()
        ? raw.boxName.trim()
        : null,
  };
}

export function normalizeBoxListItem(value: unknown): BoxListItem {
  const raw = isRecord(value) ? value : {};

  return {
    id: String(raw.id || ""),
    name: String(raw.name || ""),
    photo: typeof raw.photo === "string" ? raw.photo : "",
    typeTags: normalizeTypeTags(raw.typeTags),
    description: typeof raw.description === "string" ? raw.description : "",
    parentId: raw.parentId ? String(raw.parentId) : null,
  };
}

export function normalizeBoxProductItem(value: unknown): BoxProductItem {
  const raw = isRecord(value) ? value : {};
  const photo = typeof raw.photo === "string" ? raw.photo : "";
  const photos = normalizeProductPhotos(raw.photos, photo);

  return {
    id: String(raw.id || ""),
    name: String(raw.name || ""),
    photo: photos[0] || photo,
    photos,
    description: typeof raw.description === "string" ? raw.description : "",
    typeTags: normalizeTypeTags(raw.typeTags),
    measure: normalizeMeasure(raw.measure),
    quantity: normalizeQuantity(raw.quantity),
    parentId: raw.parentId ? String(raw.parentId) : null,
    manufacturingDate: parseDate(raw.manufacturingDate),
    expiryDate: parseDate(raw.expiryDate),
  };
}

export function normalizeDeletedProductItem(
  value: unknown,
): DeletedProductItem {
  const product = normalizeProduct(value);
  const raw = isRecord(value) ? value : {};

  return {
    ...product,
    deletedAt:
      typeof raw.deletedAt === "string" && raw.deletedAt.trim()
        ? raw.deletedAt
        : null,
    deletedBy:
      typeof raw.deletedBy === "string" && raw.deletedBy.trim()
        ? raw.deletedBy
        : null,
    deleteReason:
      typeof raw.deleteReason === "string" && raw.deleteReason.trim()
        ? raw.deleteReason
        : null,
  };
}

export function normalizeDeletedBoxItem(value: unknown): DeletedBoxItem {
  const box = normalizeBoxListItem(value);
  const raw = isRecord(value) ? value : {};

  return {
    ...box,
    deletedAt:
      typeof raw.deletedAt === "string" && raw.deletedAt.trim()
        ? raw.deletedAt
        : null,
    deletedBy:
      typeof raw.deletedBy === "string" && raw.deletedBy.trim()
        ? raw.deletedBy
        : null,
    deleteReason:
      typeof raw.deleteReason === "string" && raw.deleteReason.trim()
        ? raw.deleteReason
        : null,
  };
}

export function normalizeSoftDeleteAuditItem(
  value: unknown,
): SoftDeleteAuditItem {
  const raw = isRecord(value) ? value : {};
  const entityTypeRaw = raw.entityType;

  return {
    id: String(raw.id || ""),
    entityType: entityTypeRaw === "box" ? "box" : "product",
    entityId: String(raw.entityId || ""),
    entityName:
      typeof raw.entityName === "string" && raw.entityName.trim()
        ? raw.entityName.trim()
        : null,
    parentBoxId:
      typeof raw.parentBoxId === "string" && raw.parentBoxId.trim()
        ? raw.parentBoxId.trim()
        : null,
    actorUserId:
      typeof raw.actorUserId === "string" && raw.actorUserId.trim()
        ? raw.actorUserId.trim()
        : null,
    actorUserName:
      typeof raw.actorUserName === "string" && raw.actorUserName.trim()
        ? raw.actorUserName.trim()
        : null,
    source:
      typeof raw.source === "string" && raw.source.trim()
        ? raw.source.trim()
        : null,
    metadata: isRecord(raw.metadata)
      ? (raw.metadata as Record<string, unknown>)
      : null,
    createdAt:
      typeof raw.createdAt === "string" && raw.createdAt.trim()
        ? raw.createdAt.trim()
        : null,
  };
}

export function normalizeAuthUser(value: unknown): AuthUser {
  const raw = isRecord(value) ? value : {};
  const role = String(raw.role || "user");
  const statusRaw = String(raw.status || "active");
  const email =
    typeof raw.email === "string" && raw.email.trim() ? raw.email.trim() : null;

  return {
    id: String(raw.id || ""),
    name: String(raw.name || ""),
    email,
    photo: typeof raw.photo === "string" ? raw.photo : "",
    role:
      role === "admin" || role === "user" || role === "viewer" ? role : "user",
    status: statusRaw === "pending" ? "pending" : "active",
  };
}

export function normalizeAuthResponse(value: unknown): AuthResponse | null {
  if (!isRecord(value) || !isRecord(value.user)) {
    return null;
  }

  const accessToken = String(value.accessToken || "").trim();
  const refreshToken = String(value.refreshToken || "").trim();
  const expiresIn = Number(value.expiresIn);

  if (!accessToken || !refreshToken || !Number.isFinite(expiresIn)) {
    return null;
  }

  return {
    user: normalizeAuthUser(value.user),
    accessToken,
    refreshToken,
    expiresIn,
  };
}

export function normalizeAuthIdentity(value: unknown): AuthIdentity {
  const raw = isRecord(value) ? value : {};
  const providerRaw = String(raw.provider || "");
  const provider: AuthIdentityProvider =
    providerRaw === "telegram" ||
    providerRaw === "google" ||
    providerRaw === "apple"
      ? providerRaw
      : "email";

  return {
    provider,
    providerUserId: String(raw.providerUserId || ""),
    providerLogin:
      typeof raw.providerLogin === "string" && raw.providerLogin.trim()
        ? raw.providerLogin.trim()
        : null,
    createdAt:
      typeof raw.createdAt === "string" && raw.createdAt.trim()
        ? raw.createdAt.trim()
        : null,
    updatedAt:
      typeof raw.updatedAt === "string" && raw.updatedAt.trim()
        ? raw.updatedAt.trim()
        : null,
  };
}

export function toSafeNonNegativeInt(value: unknown, fallback: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.max(0, Math.floor(parsed));
}

export function normalizePagedPayload<T>(
  payload: unknown,
  mapItem: (value: unknown) => T,
  errorMessage: string,
): PagedResult<T> {
  if (
    !isRecord(payload) ||
    payload.success !== true ||
    !Array.isArray(payload.data)
  ) {
    const apiError = extractApiError(payload);
    throw new Error(apiError || errorMessage);
  }

  const limitValue =
    payload.limit === null || payload.limit === undefined
      ? null
      : toSafeNonNegativeInt(payload.limit, 0);

  return {
    items: payload.data.map((item) => mapItem(item)),
    total: toSafeNonNegativeInt(payload.total, payload.data.length),
    offset: toSafeNonNegativeInt(payload.offset, 0),
    limit: limitValue,
  };
}

export function normalizeDashboardStats(
  payload: unknown,
  errorMessage: string,
): DashboardStats {
  if (
    !isRecord(payload) ||
    payload.success !== true ||
    !isRecord(payload.data)
  ) {
    const apiError = extractApiError(payload);
    throw new Error(apiError || errorMessage);
  }

  const data = payload.data;

  return {
    productsTotal: toSafeNonNegativeInt(data.productsTotal, 0),
    boxesTotal: toSafeNonNegativeInt(data.boxesTotal, 0),
    productsInBoxes: toSafeNonNegativeInt(data.productsInBoxes, 0),
    productsWithoutBox: toSafeNonNegativeInt(data.productsWithoutBox, 0),
    expiringSoon: toSafeNonNegativeInt(data.expiringSoon, 0),
    expired: toSafeNonNegativeInt(data.expired, 0),
  };
}

export function normalizeAdminDashboardSummary(
  payload: unknown,
  errorMessage: string,
): AdminDashboardSummary {
  if (
    !isRecord(payload) ||
    payload.success !== true ||
    !isRecord(payload.data)
  ) {
    const apiError = extractApiError(payload);
    throw new Error(apiError || errorMessage);
  }

  const data = payload.data;
  return {
    totalUsers: toSafeNonNegativeInt(data.totalUsers, 0),
    activeUsers: toSafeNonNegativeInt(
      data.activeUsers,
      toSafeNonNegativeInt(data.totalUsers, 0),
    ),
    pendingUsers: toSafeNonNegativeInt(data.pendingUsers, 0),
    deletedProducts: toSafeNonNegativeInt(data.deletedProducts, 0),
    deletedBoxes: toSafeNonNegativeInt(data.deletedBoxes, 0),
  };
}

export function normalizeUserListItem(value: unknown): UserListItem {
  const raw = isRecord(value) ? value : {};
  const roleRaw = String(raw.role || "user");
  const statusRaw = String(raw.status || "active");
  const email =
    typeof raw.email === "string" && raw.email.trim() ? raw.email.trim() : null;

  return {
    id: String(raw.id || ""),
    name: String(raw.name || ""),
    email,
    photo: typeof raw.photo === "string" ? raw.photo : "",
    role:
      roleRaw === "admin" || roleRaw === "user" || roleRaw === "viewer"
        ? roleRaw
        : "user",
    status: statusRaw === "pending" ? "pending" : "active",
    lastActive: parseDate(raw.lastActive),
  };
}

function normalizeOptionalInputDate(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return null;
  }

  return trimmed;
}

function normalizeOptionalText(
  value: unknown,
  maxLength: number,
): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  return trimmed.slice(0, maxLength);
}

function normalizeOptionalUrl(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  try {
    new URL(trimmed);
    return trimmed;
  } catch {
    return null;
  }
}

function normalizeOptionalBoxId(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!isBoxId(trimmed)) {
    return null;
  }

  return trimmed;
}

function normalizeOptionalMeasure(value: unknown): Measures | null {
  if (value === Measures.quantity) {
    return Measures.quantity;
  }
  if (value === Measures.weight) {
    return Measures.weight;
  }
  if (value === Measures.liters) {
    return Measures.liters;
  }
  return null;
}

function normalizeOptionalPositiveNumber(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

function normalizeAiProductDraft(value: unknown): AiProductDraft {
  const raw = isRecord(value) ? value : {};

  return {
    name: normalizeOptionalText(raw.name, 100),
    description: normalizeOptionalText(raw.description, 2000),
    typeTags: normalizeTypeTags(raw.typeTags),
    parentId: normalizeOptionalBoxId(raw.parentId),
    parentHint: normalizeOptionalText(raw.parentHint, 120),
    measure: normalizeOptionalMeasure(raw.measure),
    quantity: normalizeOptionalPositiveNumber(raw.quantity),
    manufacturingDate: normalizeOptionalInputDate(raw.manufacturingDate),
    expiryDate: normalizeOptionalInputDate(raw.expiryDate),
    photo: normalizeOptionalUrl(raw.photo),
  };
}

function normalizeAiBoxDraft(value: unknown): AiBoxDraft {
  const raw = isRecord(value) ? value : {};

  return {
    name: normalizeOptionalText(raw.name, 100),
    description: normalizeOptionalText(raw.description, 2000),
    typeTags: normalizeTypeTags(raw.typeTags),
    parentId: normalizeOptionalBoxId(raw.parentId),
    parentHint: normalizeOptionalText(raw.parentHint, 120),
    photo: normalizeOptionalUrl(raw.photo),
  };
}

export function normalizeAiFormFillResult(payload: unknown): AiFormFillResult {
  if (
    !isRecord(payload) ||
    payload.success !== true ||
    !("data" in payload) ||
    !isRecord(payload.data)
  ) {
    const apiError = extractApiError(payload);
    throw new Error(
      apiError || "Некорректный ответ сервера при AI-автозаполнении формы",
    );
  }

  const entity = payload.data.entity;
  const model =
    typeof payload.data.model === "string" && payload.data.model.trim()
      ? payload.data.model.trim()
      : "unknown";

  if (entity === "product") {
    return {
      entity,
      model,
      draft: normalizeAiProductDraft(payload.data.draft),
    };
  }

  if (entity === "box") {
    return {
      entity,
      model,
      draft: normalizeAiBoxDraft(payload.data.draft),
    };
  }

  throw new Error("Некорректная сущность в ответе AI-автозаполнения");
}
